import { createFileRoute, Link } from "@tanstack/react-router";
import { EmptyState } from "@/components/brand/empty-state";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { isEnabled } from "@/config/features";
import {
  DashboardTabs,
  PillarCoverageCard,
  TeamParticipation,
  usePageSnapshot,
} from "@/features/dashboard";
import { MentionsSection } from "@/features/mentions";
import { PageReportView } from "@/features/social-page";

export const Route = createFileRoute("/_authenticated/org/analytics")({
  component: AnalyticsPage,
});

/**
 * Two kinds of number, kept apart because they come from different places
 * and mean different things: the LinkedIn Page's own data, and what the
 * team reported posting. The second is labelled self-reported everywhere.
 */
function AnalyticsPage() {
  const snapshot = usePageSnapshot();
  return (
    <div>
      <PageHeader
        eyebrow="Your team"
        title="Dashboard"
        description="How your Page is doing, and how your team is showing up."
      />
      <DashboardTabs />

      <div className="space-y-10">
        <section className="space-y-4" aria-labelledby="page-analytics">
          <div>
            <h2 id="page-analytics" className="font-semibold text-foreground text-lg">
              LinkedIn Page
            </h2>
            <p className="text-muted-foreground text-sm">
              Real data from LinkedIn about your organisation's Page, not about people.
            </p>
          </div>
          {snapshot.isPending ? (
            <Skeleton className="h-40 rounded-2xl" />
          ) : snapshot.data?.connected ? (
            <PageReportView />
          ) : (
            <EmptyState
              title="Your Page is not connected"
              description="Connect your LinkedIn Page to see how it is performing."
              action={
                <Button asChild>
                  <Link to="/org/page">Connect your Page</Link>
                </Button>
              }
            />
          )}
        </section>

        {isEnabled("brandMentionTracking") ? (
          <section className="space-y-4" aria-labelledby="brand-mentions">
            <h2 id="brand-mentions" className="font-semibold text-foreground text-lg">
              Brand mentions
            </h2>
            <MentionsSection />
          </section>
        ) : null}

        <section className="space-y-4" aria-labelledby="team-participation">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="team-participation" className="font-semibold text-foreground text-lg">
              Employee participation
            </h2>
            <Badge variant="outline">Self-reported</Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            What your team told the platform they posted. Counts only, never what anyone wrote.
          </p>
          <TeamParticipation />
        </section>

        <section className="space-y-4" aria-labelledby="pillar-coverage">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="pillar-coverage" className="font-semibold text-foreground text-lg">
              Content pillars
            </h2>
            <Badge variant="outline">Self-reported</Badge>
          </div>
          <PillarCoverageCard />
        </section>
      </div>
    </div>
  );
}
